import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AddUpdateLoanType, updateLoanById } from 'api/loanFundManager/loanApi';
import GroupButton from 'components/button/groupButton';
import CustomDatePickerForm from 'components/form/dateSelectForm/customDatePickerForm';
import { Modal } from 'flowbite-react';
import { Form, Formik, FormikProps } from 'formik';
import moment from 'moment';
import { FORMAT_DATE_API } from 'utils/constants';
import { LoanType } from 'utils/proptypes';
import {
  selectDateHandler,
} from 'utils/utils';
import * as Yup from 'yup';

import './addNonPerformingModal.scss';

type AddNonPerformingModalTypes = {
  openModal: boolean;
  setOpenModal: Function;
  selectedLoan?: LoanType;
};

type SetNonPerformingFormTypes = {
  nonPerformingDate: string;
};

const AddNonPerformingModal = (props: AddNonPerformingModalTypes) => {
  const { openModal = false, setOpenModal, selectedLoan } = props;
  const { t } = useTranslation();
  const [isResetForm, setIsResetForm] = useState(false);
  const params = useParams();
  const queryClient = useQueryClient();
  const partnerId = params.partnerId;
  const ValidateSchema = Yup.object().shape({
    nonPerformingDate: Yup.date()
      .nullable()
      .test({
        name: 'nonPerformingDate-test',
        skipAbsent: true,
        test: (value, ctx) => {
          const {
            createError,
            parent: { disbursementDate },
          } = ctx;
          if (value && value < disbursementDate) return createError({ message: t('loanPage.nonPerformingDateMin') });
          return true;
        },
      }),

  });

  const initialValues: any = useMemo(() => {
    return selectedLoan
      ? {
        nonPerformingDate: selectedLoan?.nonPerformingDate ? moment(selectedLoan?.nonPerformingDate).toDate() : '',
      }
      : {
        nonPerformingDate: '',
      };
  }, [selectedLoan]);

  const handleUpdateLoan = async (loanPayload: AddUpdateLoanType) => {
    await updateLoanById(selectedLoan?.id as string, loanPayload);
  };

  const mutation = useMutation('set-non-performing-loan', {
    mutationFn: handleUpdateLoan,
  });

  const handleSubmit = (
    data: SetNonPerformingFormTypes,
    action: {
      [key: string]: any;
    },
  ) => {
    const successCallback = async (message: string) => {
      setOpenModal(!openModal);
      toast.success(message);
      action.resetForm();
      await queryClient.invalidateQueries(['loan']);
    };

    if (openModal) {
      mutation.mutate(
        {
          partnerId,
          nonPerformingDate: data.nonPerformingDate ? moment(data.nonPerformingDate).format(FORMAT_DATE_API) : null,
        } as any,
        {
          onSuccess: async () => {
            const message: string = t('loanPage.setNonPerformingSuccessMessage');
            await successCallback(message);
          },
          onError: async (error: any) => {
            const message: string = `${error.response.data.errors?.[0].detail}`;
            toast.error(message);
          },
        },
      );
    }
  };

  const closeModalHandler = (props: FormikProps<SetNonPerformingFormTypes>) => {
    setOpenModal(!openModal);
    props.resetForm();
    props.setErrors({});
  };

  useEffect(() => {
    if (openModal) {
      setIsResetForm(!isResetForm);
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, setIsResetForm]);

  return (
    <Modal
      id="add-loan-non-performing-modal"
      show={openModal}
      size="md"
      onClose={() => {
        setOpenModal(!openModal);
        setIsResetForm(!isResetForm);
      }}
      dismissible={true}
    >
      <Modal.Header>{t('Set Non-Performing')}</Modal.Header>
      <Modal.Body>
        <Formik enableReinitialize onSubmit={handleSubmit} initialValues={initialValues}
          validationSchema={ValidateSchema} validateOnChange={true}>
          {(props: FormikProps<any>) => {
            return (
              <Form className="flex flex-col gap-4">
                <div className="space-y-4 px-4 pb-2 sm:pb-6 lg:px-2 xl:pb-8">
                  <div className="form-flex-col flex justify-between items-start space-x-2">
                    <CustomDatePickerForm
                      id="nonPerformingDate"
                      name="nonPerformingDate"
                      label={t('Non-Performing Date')}
                      placeHolder={t('Non-Performing Date')}
                      defaultValue={props.getFieldProps('nonPerformingDate').value}
                      propsFormik={props}
                      selectItemsHandler={(date: Date) =>
                        selectDateHandler<SetNonPerformingFormTypes>({
                          date,
                          props,
                          fieldName: 'nonPerformingDate',
                        })
                      }
                      isRequired={false}
                      isDeleteSelectedDate={openModal}
                      minDate={moment(selectedLoan?.disbursementDate).toDate()}
                      maxDate={moment(selectedLoan?.maturity).toDate()}
                    />
                  </div>
                </div>
                <GroupButton
                  className="w-full gap-2 justify-center pb-2"
                  buttons={[
                    {
                      type: 'button',
                      text: t('modal.cancel'),
                      classType: 'white',
                      action: () => closeModalHandler(props),
                    },
                    {
                      type: 'submit',
                      text: t('modal.save'),
                      classType: 'blue',
                    },
                  ]}
                />
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default AddNonPerformingModal;
